import axios, { AxiosError, AxiosRequestConfig } from "axios";
import ErrorLoggingService from "./ErrorLoggingService";
import { extractDownloadFilename } from "../utils/FormatUtils";

const baseUrl =
  process.env.REACT_APP_INTIZA_COMPANY_AUTH ??
  process.env.REACT_APP_INTIZA_COMPANY_AUTH + "";
type TData = {};
type RequestPromiseExtraProperties = {
  abortController: AbortController;
  endpoint: string;
};
export type RequestPromise<TResponse> = Promise<
  TResponse | AxiosError<TResponse, TData>
> &
  RequestPromiseExtraProperties;
class RequestPortalService {
  public static getBaseUrl() {
    return baseUrl;
  }
  public static currentAbortController = new AbortController();
  public static get<TResponse = {}>(
    endpoint: string,
    customerToken?: string,
    requestBaseUrl: string = baseUrl
  ): Promise<TResponse | AxiosError<TResponse, TData>> {
    const headers: {
      Authorization?: string;
      "Content-Type": string;
    } = {
      "Content-Type": "application/json"
    };
    if (customerToken) {
      headers.Authorization = "Bearer " + customerToken;
    }

    const abortController = new AbortController();
    RequestPortalService.currentAbortController = abortController;

    const axiosRequest = Promise.resolve().then(() => {
      const config: AxiosRequestConfig<TData> = {
        method: "get",
        timeout: 30000,
        url: requestBaseUrl + endpoint,
        headers,
        signal: abortController.signal
      };
      const response = axios(config)
        .catch((x: AxiosError<TResponse, TData>) => {
          // Log request errors
          ErrorLoggingService.logRequestError(x, requestBaseUrl + endpoint, {});
          return x;
        })
        .then((x) => {
          if (x instanceof Error) {
            return x;
          }
          const interceptions =
            RequestPortalService.interceptList[endpoint]?.splice(0, 9999) ?? [];
          interceptions.forEach((inter) => inter(x));
          return x;
        });
      return response;
    });
    const request = Object.assign(axiosRequest, {
      abortController,
      endpoint
    }) as RequestPromise<TResponse>;
    return request;
  }
  public static post<TResponse = {}>(
    endpoint: string,
    data: TData,
    customerToken?: string,
    requestBaseUrl: string = baseUrl
  ): Promise<TResponse | AxiosError<TResponse, TData>> {
    const headers: {
      Authorization?: string;
      "Content-Type": string;
    } = {
      "Content-Type": "application/json"
    };
    if (customerToken) {
      headers.Authorization = "Bearer " + customerToken;
    }

    const abortController = new AbortController();
    RequestPortalService.currentAbortController = abortController;

    const axiosRequest = Promise.resolve().then(() => {
      const config: AxiosRequestConfig<TData> = {
        method: "post",
        timeout: 30000,
        url: requestBaseUrl + endpoint,
        headers,
        data,
        signal: abortController.signal
      };
      const response = axios(config)
        .catch((x: AxiosError<TResponse, TData>) => {
          // Log request errors
          ErrorLoggingService.logRequestError(
            x,
            requestBaseUrl + endpoint,
            data
          );
          return x;
        })
        .then((x) => {
          if (x instanceof Error) {
            return x;
          }
          const interceptions =
            RequestPortalService.interceptList[endpoint]?.splice(0, 9999) ?? [];
          interceptions.forEach((inter) => inter(x));
          return x;
        });
      return response;
    });
    const request = Object.assign(axiosRequest, {
      abortController,
      endpoint
    }) as RequestPromise<TResponse>;
    return request;
  }
  public static async downloadFilePost(
    fileDownloadUrl: string,
    fileId: string,
    fileName = "export.xlsx",
    customerToken: string
  ): Promise<Error | unknown> {
    const headers: { Authorization?: string; "Content-Type": string } = {
      "Content-Type": "application/json"
    };
    if (!customerToken) {
      return Error("no auth token");
    }
    headers.Authorization = customerToken;
    try {
      const response = await axios({
        url: baseUrl + fileDownloadUrl,
        method: "POST",
        responseType: "blob",
        headers
      });
      const name = extractDownloadFilename(
        response.headers["content-disposition"],
        fileName
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      if (link && typeof link.remove === "function") {
        try {
          link.remove();
        } catch (e) {
          console.warn("No se pudo remover el enlace de descarga del DOM:", e);
        }
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Log download errors
      if (error instanceof Error) {
        ErrorLoggingService.logError(error, baseUrl + fileDownloadUrl, {
          fileName,
          fileId
        });
      }
      return Error("status error");
    }
  }

  public static async downloadFile(
    fileDownloadUrl: string,
    customerToken: string,
    fileName: string = "export.xlsx"
  ): Promise<Error | unknown> {
    const headers: { Authorization?: string; "Content-Type": string } = {
      "Content-Type": "application/json"
    };
    if (!customerToken) {
      return Error("no auth token");
    }
    headers.Authorization = customerToken;
    try {
      const response = await axios({
        url: baseUrl + fileDownloadUrl,
        method: "GET",
        responseType: "blob",
        headers
      });
      const name = extractDownloadFilename(
        response.headers["content-disposition"],
        fileName
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      if (link && typeof link.remove === "function") {
        try {
          link.remove();
        } catch (e) {
          console.warn("No se pudo remover el enlace de descarga del DOM:", e);
        }
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Log download errors
      if (error instanceof Error) {
        ErrorLoggingService.logError(error, baseUrl + fileDownloadUrl, {
          fileName,
          fileId
        });
      }
      return Error("status error");
    }
  }
  private static interceptList: Record<string, ((result: any) => void)[]> = {};
  static intercept(
    path: string,
    func: (typeof RequestPortalService.interceptList)[string][number]
  ) {
    this.interceptList[path] = this.interceptList[path] ?? [];
    this.interceptList[path].push(func);
  }
}

// used for testing purposes :)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default RequestPortalService;
